/* eslint-disable max-len */
import React from 'react';
import { Box, Typography, IconButton, Link } from '@mui/material';
import { Info } from '@mui/icons-material';
import { CustomTooltip } from './CustomTooltip/CustomTooltip';

interface IDXAttributionProps {
  source?: string;
  listingFirm?: string;
  listingAgent?: string;
  listingAgentPhone?: string;
  listingId?: string;
  compact?: boolean;
  showDisclaimer?: boolean;
  className?: string;
  dataTimestamp?: string;
}

// Realtracs logo URL (MLS Grid Rule 23)
const REALTRACS_LOGO_URL =
  'https://wp-assets.cdn.realtracs.com/wp-content/uploads/2024/12/cropped-logo.png';

// FMLS IDX logo URL (Rule 13.7.d.1)
const FMLS_IDX_LOGO_URL = 'https://www.fmls.com/media/2480/greenidx.png';

// Technical entity contact (FMLS Rule 13.8, GALMLS 18.2.9)
const DATA_ACCURACY_EMAIL = 'hello@homepoint.co';
const TECHNICAL_ENTITY = 'Homepoint Brokerage LLC';

const IDXAttribution: React.FC<IDXAttributionProps> = ({
  source = 'GALMLS',
  listingFirm,
  listingAgent,
  listingAgentPhone,
  listingId,
  compact = false,
  showDisclaimer = true,
  className,
  dataTimestamp,
}) => {
  const isMlsGridSource = source === 'REALTRACS';
  const isFmlsSource = source === 'FMLS';
  // Get display name for the MLS source
  const getSourceDisplayName = () => {
    switch (source) {
      case 'EASTTN':
        return 'East Tennessee REALTORS® MLS';
      case 'VALLEYMLS':
        return 'Valley MLS';
      case 'REALTRACS':
        return 'Realtracs MLS';
      case 'FMLS':
        return 'First Multiple Listing Service';
      case 'GALMLS':
      default:
        return 'Greater Alabama MLS';
    }
  };

  // Format timestamp to local time
  const formatTimestamp = (timestamp?: string): string => {
    if (!timestamp) return new Date().toLocaleString();
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return new Date().toLocaleString();
    return date.toLocaleString();
  };

  // Different disclaimers for different MLS sources
  const getDisclaimerText = () => {
    const mlsName = getSourceDisplayName();
    if (isMlsGridSource) {
      const timestamp = formatTimestamp(dataTimestamp);
      // MLS Grid Rule 24 required disclaimer
      return `Based on information submitted to the MLS GRID as of ${timestamp}. All data is obtained from various sources and may not have been verified by broker or MLS GRID. Supplied Open House Information is subject to change without notice. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information. IDX information is provided exclusively for consumers' personal, non-commercial use and may not be used for any purpose other than to identify prospective properties consumers may be interested in purchasing.`;
    }
    if (isFmlsSource) {
      // FMLS Rule 13.d.2 required disclaimer for commingled listings with DMCA notice
      return `Listings identified with the FMLS IDX logo come from FMLS and are held by brokerage firms other than the owner of this website. The listing brokerage is identified in any listing details. Information is deemed reliable but is not guaranteed. If you believe any FMLS listing contains material that infringes your copyrighted work, please review our DMCA policy at homepoint.co/terms. © ${new Date().getFullYear()} First Multiple Listing Service, Inc.`;
    }
    return `IDX information is provided exclusively for consumers' personal, non-commercial use and may not be used for any purpose other than to identify prospective properties consumers may be interested in purchasing. Data is deemed reliable but not guaranteed accurate by the ${mlsName}. Report data accuracy issues to ${DATA_ACCURACY_EMAIL}.`;
  };

  // MLS Grid Rule 23 - source attribution text
  const getSourceAttribution = () => {
    if (isMlsGridSource) {
      return `Listing courtesy of ${getSourceDisplayName()} as distributed by MLS GRID`;
    }
    if (isFmlsSource) {
      // FMLS Rule 13.7.c - "Listing Provided By" or "Listing Courtesy Of"
      return 'Listing Courtesy of FMLS';
    }
    return `Courtesy of ${getSourceDisplayName()}`;
  };

  const disclaimerText = getDisclaimerText();

  if (compact) {
    // Compact version for cards and smaller displays
    return (
      <Box
        className={className}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderTop: '1px solid #e5e7eb',
          pt: 1,
          mt: 1,
        }}
      >
        {isMlsGridSource && (
          <Box
            component="img"
            src={REALTRACS_LOGO_URL}
            alt="Realtracs MLS"
            sx={{ height: 20, mr: 1, flexShrink: 0 }}
          />
        )}
        {isFmlsSource && (
          <Box
            component="img"
            src={FMLS_IDX_LOGO_URL}
            alt="FMLS IDX"
            sx={{ height: 20, mr: 1, flexShrink: 0 }}
          />
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <Typography
            sx={{
              fontSize: 11,
              color: '#6b7280',
              lineHeight: 1.3,
            }}
          >
            {getSourceAttribution()}
            {listingFirm && (
              <>
                {' • '}
                <span style={{ fontWeight: 500 }}>{listingFirm}</span>
              </>
            )}
          </Typography>
          {listingAgent && (
            <Typography sx={{ fontSize: 11, color: '#6b7280' }}>
              Listed by: {listingAgent}
              {listingAgentPhone && ` • ${listingAgentPhone}`}
            </Typography>
          )}
          {listingId && (
            <Typography sx={{ fontSize: 10, color: '#9ca3af' }}>
              MLS# {listingId}
            </Typography>
          )}
        </Box>
        {showDisclaimer && (
          <CustomTooltip
            title={disclaimerText}
            placement="top"
            width="300px"
            arrow
          >
            <IconButton size="small" sx={{ ml: 1, p: 0.5 }}>
              <Info sx={{ fontSize: 14, color: '#9ca3af' }} />
            </IconButton>
          </CustomTooltip>
        )}
      </Box>
    );
  }

  // Full version for property detail views
  return (
    <Box
      className={className}
      sx={{
        borderTop: '1px solid #e5e7eb',
        pt: 1.5,
        mt: 1.5,
      }}
    >
      {isMlsGridSource && (
        <Box sx={{ mb: 1 }}>
          <Box
            component="img"
            src={REALTRACS_LOGO_URL}
            alt="Realtracs MLS"
            sx={{ height: 24 }}
          />
        </Box>
      )}
      {isFmlsSource && (
        <Box sx={{ mb: 1 }}>
          <Box
            component="img"
            src={FMLS_IDX_LOGO_URL}
            alt="FMLS IDX"
            sx={{ height: 24 }}
          />
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 12, color: '#6b7280', mb: 0.5 }}>
            <strong>Listing Information</strong>
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#6b7280' }}>
            {getSourceAttribution()}
          </Typography>
          {listingId && (
            <Typography sx={{ fontSize: 12, color: '#6b7280' }}>
              MLS# {listingId}
            </Typography>
          )}
          {listingFirm && (
            <Typography sx={{ fontSize: 12, color: '#6b7280' }}>
              Listing Firm: {listingFirm}
            </Typography>
          )}
          {listingAgent && (
            <Typography sx={{ fontSize: 12, color: '#6b7280' }}>
              Listed by: {listingAgent}
              {listingAgentPhone && ` • ${listingAgentPhone}`}
            </Typography>
          )}
          {dataTimestamp && (
            <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.5 }}>
              Data last updated: {formatTimestamp(dataTimestamp)}
            </Typography>
          )}
        </Box>
      </Box>
      {showDisclaimer && (
        <Typography
          component="div"
          sx={{
            fontSize: 10,
            color: '#9ca3af',
            mt: 1,
            lineHeight: 1.4,
            fontStyle: 'italic',
          }}
        >
          {isMlsGridSource ? (
            <>
              Based on information submitted to the MLS GRID.
              <br />© {new Date().getFullYear()} MLS GRID. All rights reserved.
            </>
          ) : isFmlsSource ? (
            <>
              Listings identified with the FMLS IDX logo come from FMLS and are
              held by brokerage firms other than the owner of this website. The
              listing brokerage is identified in any listing details.
              Information is deemed reliable but is not guaranteed. If you
              believe any FMLS listing contains material that infringes your
              copyrighted work please{' '}
              <Link
                href="/terms"
                target="_blank"
                sx={{ fontSize: 10, color: '#6b7280' }}
              >
                click here
              </Link>{' '}
              to review our DMCA policy and learn how to submit a takedown
              request.
              <br />© {new Date().getFullYear()} First Multiple Listing Service,
              Inc.
            </>
          ) : (
            <>
              Based on information from the {getSourceDisplayName()}.
              <br />© {new Date().getFullYear()} {getSourceDisplayName()}. All
              rights reserved.
            </>
          )}
        </Typography>
      )}

      {/* Technical Entity Disclosure (FMLS Rule 13.8) & Data Accuracy Contact (GALMLS 18.2.9) */}
      <Typography
        sx={{
          fontSize: 9,
          color: '#9ca3af',
          mt: 1,
          lineHeight: 1.3,
        }}
      >
        Technical provider: {TECHNICAL_ENTITY} •{' '}
        <Link
          href={`mailto:${DATA_ACCURACY_EMAIL}?subject=Data Accuracy Issue`}
          sx={{ fontSize: 9, color: '#6b7280' }}
        >
          Report data issues
        </Link>
      </Typography>
    </Box>
  );
};

export default IDXAttribution;
